import { useEffect, useMemo, useState } from "react";
import { Button, FormLayout, Heading, Text, Textarea } from "@/ui";
import styles from "./SchedulesFormJSON.module.scss";

const getScheduleData = (context, inputs) => {
    if (context === "Delivery") {
        return inputs?.delivery_schedules;
    }

    if (context === "Closures") {
        return inputs?.closures;
    }

    if (context === "Curbside") {
        return inputs?.curbside_schedules;
    }

    return inputs?.pickup_schedules;
};

const toJsonText = data => {
    if (data?.length > 0) {
        return JSON.stringify(data, null, 2);
    }

    return "";
};

const SchedulesFormJSON = ({
    context,
    handleCancel,
    handlerUpdatingOrCreate,
    inputs,
    setInputs,
    isUpdating,
}) => {
    const [draft, setDraft] = useState(() =>
        toJsonText(getScheduleData(context, inputs)),
    );
    const [parseError, setParseError] = useState(null);

    const title = useMemo(() => {
        if (context === "Delivery") {
            return inputs?.delivery_schedules?.length
                ? "Update Delivery Schedules"
                : "Add Delivery Schedules";
        }

        if (context === "Closures") {
            return inputs?.closures?.length
                ? "Update Closures"
                : "Add Closures";
        }

        if (context === "Curbside") {
            return inputs?.curbside_schedules?.length
                ? "Update Curbside Schedules"
                : "Add Curbside Schedules";
        }

        return inputs?.pickup_schedules?.length
            ? "Update Pickup Schedules"
            : "Add Pickup Schedules";
    }, [context, inputs]);

    useEffect(() => {
        setDraft(toJsonText(getScheduleData(context, inputs)));
        setParseError(null);
    }, [context]);

    const applyJson = value => {
        setDraft(value);

        try {
            const jsonObject = value.trim() ? JSON.parse(value) : [];

            setParseError(null);

            if (context === "Delivery") {
                setInputs({ ...inputs, delivery_schedules: jsonObject });
                return;
            }

            if (context === "Closures") {
                setInputs({ ...inputs, closures: jsonObject });
                return;
            }

            if (context === "Curbside") {
                setInputs({ ...inputs, curbside_schedules: jsonObject });
                return;
            }

            setInputs({ ...inputs, pickup_schedules: jsonObject });
        } catch (error) {
            setParseError("Invalid JSON");
        }
    };

    return (
        <div className={styles.root}>
            <FormLayout>
                <Heading level={5}>{title}</Heading>
                <FormLayout.Section>
                    <Text tone="secondary" variant="body-sm">
                        Edit the schedule array as JSON, then save to apply
                        changes.
                    </Text>
                    <Textarea
                        error={parseError ?? undefined}
                        fullWidth
                        label="Schedule JSON"
                        name="schedule"
                        onChange={event => applyJson(event.target.value)}
                        rows={24}
                        textareaClassName={styles.mono}
                        value={draft}
                    />
                    <div className={styles.actions}>
                        <Button
                            disabled={isUpdating}
                            onClick={handleCancel}
                            type="button"
                            variant="secondary"
                        >
                            Cancel
                        </Button>
                        <Button
                            disabled={Boolean(parseError)}
                            loading={isUpdating}
                            onClick={() => handlerUpdatingOrCreate(inputs)}
                            type="button"
                        >
                            Save
                        </Button>
                    </div>
                </FormLayout.Section>
            </FormLayout>
            <div aria-hidden className={styles.separator} />
        </div>
    );
};

export default SchedulesFormJSON;
